import { CSSProperties, ForwardedRef, forwardRef } from 'react';
import cls from 'classnames';

import { PreviewFragment } from '@/media/types/PreviewResponse';

import CollectionPreviewFragment from './CollectionPreviewFragment/CollectionPreviewFragment';
import styles from './CollectionPreviewGrid.module.css';
import { FRAGMENTS_COUNT } from './CollectionPreviewGrid.utils';

export interface CollectionPreviewGridProps {
  fragments: PreviewFragment[];
  containerSize: number;
  gap: number;
  isBordered?: boolean;
  isSkeletonShown?: boolean;
  isRenderLabelExpandable?: boolean;
}

function CollectionPreviewGrid(props: CollectionPreviewGridProps, ref: ForwardedRef<HTMLDivElement>) {
  const {
    fragments,
    containerSize,
    gap,
    isBordered = false,
    isSkeletonShown = false,
    isRenderLabelExpandable = true,
  } = props;

  const fragmentSize = (containerSize - gap) / 2;

  if (isSkeletonShown) {
    return (
      <div
        ref={ref}
        className={styles.container}
        style={{ gap, '--fragment-size': `${fragmentSize}px` } as CSSProperties}
      >
        {Array.from({ length: FRAGMENTS_COUNT }).map((_, index) => (
          <div className={cls(styles.skeleton, { [styles.bordered]: isBordered })} key={index} />
        ))}
      </div>
    );
  }

  return (
    <div
      aria-label="Collection preview"
      ref={ref}
      className={styles.container}
      style={{ gap, '--fragment-size': `${fragmentSize}px` } as CSSProperties}
    >
      {fragments.map((fragment, index) => (
        <CollectionPreviewFragment
          key={index}
          fragment={fragment}
          size={fragmentSize}
          isBordered={isBordered}
          isRenderLabelExpandable={isRenderLabelExpandable}
        />
      ))}

      {fragments.length < FRAGMENTS_COUNT &&
        Array.from({ length: FRAGMENTS_COUNT - fragments.length }).map((_, index) => (
          <div className={cls(styles.placeholder, { [styles.bordered]: isBordered })} key={index} />
        ))}
    </div>
  );
}

export default forwardRef(CollectionPreviewGrid);
